import React from 'react';
import { Box, Typography } from '@mui/material';
import { isFulfilled } from '@reduxjs/toolkit';
import { useAppDispatch } from 'store/store';
import { submitDrawing, approveDrawing, rejectDrawing, resetApproval } from 'store/actions/workflowActions';
import { usePermissions } from 'hooks/usePermissions';
import { WorkflowStatusId } from 'types/enums/WorkflowStatus';
import type { IDrawing } from 'types/IDrawing';
import type { CustomMenuRow } from 'components/CustomMenu/CustomMenu';
import { CustomDialog } from 'components/dialog/CustomDialog';
import { CustomTextField } from 'components/FormFields/CustomTextField/CustomTextField';
import RedButton from 'Styled/RedButton';

type WorkflowAction = 'submit' | 'approve' | 'reject' | 'reset';

interface ActionSpec {
  label: string;
  title: string;
  question: string;
  confirmLabel: string;
  /** Reject and reset require a reason; the backend rejects an empty one. */
  needsReason: boolean;
}

const ACTIONS: Record<WorkflowAction, ActionSpec> = {
  submit: {
    label: 'Zur Freigabe vorlegen',
    title: 'Zur Freigabe vorlegen',
    question: 'Diese Ziehung zur Freigabe vorlegen?',
    confirmLabel: 'Vorlegen',
    needsReason: false,
  },
  approve: {
    label: 'Freigeben',
    title: 'Ziehung freigeben',
    question: 'Diese Ziehung freigeben? Danach ist sie nicht mehr bearbeitbar.',
    confirmLabel: 'Freigeben',
    needsReason: false,
  },
  reject: {
    label: 'Zurückweisen',
    title: 'Ziehung zurückweisen',
    question: 'Diese Ziehung zurückweisen? Die Begründung geht per E-Mail an die einreichende Person.',
    confirmLabel: 'Zurückweisen',
    needsReason: true,
  },
  reset: {
    label: 'Freigabe zurücksetzen',
    title: 'Freigabe zurücksetzen',
    question: 'Die Freigabe zurücksetzen? Die Ziehung wird dadurch wieder bearbeitbar und muss erneut freigegeben werden.',
    confirmLabel: 'Zurücksetzen',
    needsReason: true,
  },
};

/**
 * Workflow transitions driven straight from a drawings table: the menu entries
 * a row may show, and the dialogs behind them. Every transition asks for
 * confirmation first, and the two that need a reason collect it in the same
 * dialog.
 *
 * `onChanged` runs after a successful transition so the caller can reload.
 */
export const useWorkflowActions = (onChanged: () => void) => {
  const dispatch = useAppDispatch();
  const { isRedakteur, isFreigeber } = usePermissions();

  const [pending, setPending] = React.useState<{ drawingId: number; action: WorkflowAction } | null>(null);
  const [reason, setReason] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  const closeConfirm = () => {
    setPending(null);
    setReason('');
  };

  const run = async () => {
    if (!pending) return;
    const { drawingId, action } = pending;

    setBusy(true);
    const result = await dispatch(
      action === 'submit'
        ? submitDrawing({ drawingId })
        : action === 'approve'
          ? approveDrawing({ drawingId })
          : action === 'reject'
            ? rejectDrawing({ drawingId, reason })
            : resetApproval({ drawingId, reason })
    );
    setBusy(false);

    if (isFulfilled(result)) {
      closeConfirm();
      onChanged();
    }
  };

  const menuItemsFor = (drawing: IDrawing): CustomMenuRow[] => {
    const status = drawing.workflowDrawings?.[0]?.workflowStatusId;
    const rows: CustomMenuRow[] = [];

    const offer = (action: WorkflowAction) =>
      rows.push({ name: ACTIONS[action].label, onClick: () => setPending({ drawingId: drawing.id, action }) });

    // Same states the backend accepts: a new drawing and a reopened one are
    // both OFFEN.
    if (isRedakteur() && (!status || status === WorkflowStatusId.OFFEN || status === WorkflowStatusId.ZURUECKGEWIESEN)) {
      offer('submit');
    }
    if (isFreigeber() && status === WorkflowStatusId.ZUR_FREIGABE_VORGELEGT) {
      offer('approve');
      offer('reject');
    }
    if (isFreigeber() && status === WorkflowStatusId.FREIGEGEBEN) {
      offer('reset');
    }

    return rows;
  };

  const spec = pending ? ACTIONS[pending.action] : null;
  const confirmDisabled = busy || (spec?.needsReason === true && reason.trim() === '');

  const dialogs = (
    <>
      {spec && (
        <CustomDialog
          open
          title={spec.title}
          handleClose={closeConfirm}
          actions={
            <RedButton onClick={() => void run()} disabled={confirmDisabled}>
              {spec.confirmLabel}
            </RedButton>
          }
        >
          <Box className="psf-dialog__body">
            <Typography>{spec.question}</Typography>
            {spec.needsReason && (
              <Box sx={{ marginTop: 3 }}>
                <CustomTextField name="workflow-reason" label="Begründung" value={reason} onChange={(event) => setReason(event.target.value)} multiline rows={3} />
              </Box>
            )}
          </Box>
        </CustomDialog>
      )}

    </>
  );

  return { menuItemsFor, dialogs };
};
